import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Ban } from "lucide-react";
import type { UploadedTarget } from "@/components/managed-campaign/ManagedCampaignData";

interface Props {
  targets: UploadedTarget[];
  onRemove: (kept: UploadedTarget[]) => void;
  extraCompany?: string[];
  extraPerson?: string[];
  companyProfileId?: string;
  campaignKnowledgeId?: string;
  guestToken?: string;
  workspaceId?: string | null;
}

const norm = (a: string[]) => Array.from(new Set(a.map((s) => s.trim().toLowerCase()).filter((s) => s.length >= 2)));

/** Matches uploaded people against the never-contact list and lets the customer drop them before submitting. */
export function matchesExclusion(t: UploadedTarget, company: string[], person: string[]) {
  const co = String(t.company ?? "").toLowerCase();
  const ti = String(t.title ?? "").toLowerCase();
  return (!!co && company.some((e) => co.includes(e))) || (!!ti && person.some((e) => ti.includes(e)));
}

export function ExclusionMatchWarning({ targets, onRemove, extraCompany = [], extraPerson = [], companyProfileId, campaignKnowledgeId, guestToken, workspaceId }: Props) {
  const [saved, setSaved] = useState<{ company: string[]; person: string[] }>({ company: [], person: [] });

  useEffect(() => {
    if (!companyProfileId || !campaignKnowledgeId) return;
    let alive = true;
    supabase.functions.invoke("company-profile-builder", {
      body: { action: "audience_get", workspaceId: guestToken ? null : workspaceId ?? null, guestToken: guestToken ?? null, profileId: companyProfileId, campaignKnowledgeId },
    }).then(({ data }) => { if (alive && data?.exclusions) setSaved({ company: data.exclusions.company ?? [], person: data.exclusions.person ?? [] }); })
      .catch(() => { /* warning is optional */ });
    return () => { alive = false; };
  }, [companyProfileId, campaignKnowledgeId, guestToken, workspaceId]);

  const company = useMemo(() => norm([...saved.company, ...extraCompany]), [saved, extraCompany]);
  const person = useMemo(() => norm([...saved.person, ...extraPerson]), [saved, extraPerson]);
  const matched = useMemo(() => targets.filter((t) => matchesExclusion(t, company, person)), [targets, company, person]);

  if (!matched.length) return null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <div className="flex items-start gap-2">
        <Ban className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
        <div>
          <div className="font-medium">{matched.length} {matched.length === 1 ? "person matches" : "people match"} your never-contact list</div>
          <div className="text-xs text-muted-foreground">e.g. {matched.slice(0, 3).map((t) => t.company || t.title).filter(Boolean).join(", ")}</div>
        </div>
      </div>
      <Button size="sm" variant="destructive" onClick={() => onRemove(targets.filter((t) => !matchesExclusion(t, company, person)))}>Remove them</Button>
    </div>
  );
}
